import React, { useState, useEffect } from 'react';
import { DollarSign, Search, Download, Save, RefreshCw, Edit, Trash2, AlertTriangle, X, CheckCircle, List } from 'lucide-react';
import MainService from '../../services/MainService';

const Expenses = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredRows, setFilteredRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    isForDriver: false,
  });

  useEffect(() => {
    loadRows();
  }, []);

  // Toast notification effect
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await MainService.ExpensesInGrid({ Page: 1, PageSize: 100 });
      const list = (response?.Data || response || []).map((x, i) => ({
        id: x.Id || i + 1,
        name: x.Name || '',
        isForDriver: !!x.IsForDriver,
      }));
      setRows(list);
      setFilteredRows(list);
    } catch (err) {
      console.error('Load expenses error:', err);
      setError('Failed to load expenses');
      setRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setFormData({ name: '', isForDriver: false });
    setEditing(null);
  };

  const handleRefresh = async () => {
    resetForm();
    setSearchTerm('');
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await loadRows();
    } catch (err) {
      console.error('Error refreshing expenses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError('Enter Name'); return; }
    try {
      setLoading(true);
      const payload = { Name: formData.name.trim(), IsForDriver: !!formData.isForDriver };
      if (editing?.id) payload.Id = editing.id;
      const resp = await MainService.SaveExpenses(payload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        setSuccess(editing ? 'Expense updated successfully' : 'Expense created successfully');
        await loadRows();
        resetForm();
      } else {
        setError('Failed to save expense');
      }
    } catch (err) {
      console.error('Save expense error:', err);
      setError('Failed to save expense');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({ name: row.name || '', isForDriver: !!row.isForDriver });
    setError('');
    setSuccess('');
  };

  const handleDelete = (row) => { setRowToDelete(row); setDeleteDialogOpen(true); };

  const confirmDelete = async () => {
    if (!rowToDelete) return;
    try {
      setLoading(true);
      setDeleteDialogOpen(false);
      const resp = await MainService.DeleteExpense(rowToDelete.id);
      if (resp === 'Success' || resp === true || resp === 'true') {
        setSuccess('Expense deleted successfully');
        await loadRows();
      } else {
        setError(resp || 'Failed to delete expense');
      }
    } catch (err) {
      console.error('Delete expense error:', err);
      setError('Failed to delete expense');
    } finally {
      setLoading(false);
      setRowToDelete(null);
    }
  };

  const cancelDelete = () => { setDeleteDialogOpen(false); setRowToDelete(null); };

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = rows.filter((x) => (x.name || '').toLowerCase().includes(value));
    setFilteredRows(filtered);
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[field];
      const bVal = b[field];
      if (direction === 'asc') return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });
    setFilteredRows(sorted);
  };

  const handleExportExcel = () => {
    try {
      const headers = ['Serial No', 'Name', 'Is For Driver'];
      const csvContent = [
        headers.join(','),
        ...filteredRows.map((row, index) => [
          index + 1,
          `"${row.name}"`,
          `"${row.isForDriver ? 'Yes' : 'No'}"`
        ].join(','))
      ].join('\n');

      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csvContent;
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `expenses_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess('Expenses data exported successfully');
    } catch (err) {
      console.error('Error exporting data:', err);
      setError('Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Form Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center mb-2">
            <DollarSign className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">Expenses</h1>
          </div>
          <p className="text-sm text-gray-500 ml-8">Manage expense types</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-12 gap-4 items-end">
            <div className="col-span-3">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent"
                placeholder=""
              />
            </div>

            <div className="col-span-3 flex items-center gap-2">
              <input
                type="checkbox"
                id="isForDriver"
                name="isForDriver"
                checked={formData.isForDriver}
                onChange={handleInputChange}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <label htmlFor="isForDriver" className="text-sm font-medium text-gray-700">
                Is For Driver?
              </label>
            </div>

            <div className="col-span-3 flex items-center gap-2">
              <button
                type="submit"
                disabled={loading}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={editing ? 'Update' : 'Save'}
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
              </button>
              <button
                type="button"
                onClick={handleRefresh}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <List className="w-5 h-5 text-primary mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Expenses List</h3>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-8 pr-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-primary bg-transparent w-64"
              />
            </div>
            <button
              onClick={handleExportExcel}
              className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              title="Export Excel"
            >
              <Download className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-x-auto max-h-96 overflow-y-auto relative">
          {loading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 backdrop-blur-sm z-20 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <RefreshCw className="w-8 h-8 text-primary animate-spin" />
                <span className="text-sm text-gray-600">Loading...</span>
              </div>
            </div>
          )}
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th
                  onClick={() => handleSort('id')}
                  className="w-[10%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    #
                    {sortField === 'id' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('name')}
                  className="w-[40%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    Name
                    {sortField === 'name' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('isForDriver')}
                  className="w-[30%] px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    Is For Driver
                    {sortField === 'isForDriver' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="w-[20%] px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-4 py-6 text-center text-gray-500">
                    No expenses found
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                      {row.name}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${row.isForDriver ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {row.isForDriver ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(row)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(row)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Total Expenses: {filteredRows.length}</span>
            <span>Showing {filteredRows.length} of {rows.length} expenses</span>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="bg-red-50 border-b border-red-200 px-6 py-4 rounded-t-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-red-900">Confirm Delete</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700 mb-2">
                Are you sure you want to delete the expense <strong>"{rowToDelete?.name}"</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone and will permanently remove the expense from the system.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
